import { collection, documentId, query, where } from 'firebase/firestore';
import type { Query, QueryConstraint } from 'firebase/firestore';

import { db } from '@src/firebase/firebase';

/** Firestore rejects `in` / `not-in` filters with more than 30 values. */
export const FIRESTORE_IN_LIMIT = 30;

export const chunkIds = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export interface DocIdQueryTarget {
  /**
   * Collection path segments, e.g.
   * `[FSCollections.ORGANIZATIONS, orgId, FSCollections.DIVISIONS, divId, FSCollections.SCHEDULES]`.
   * One target per collection: org-wide lists have a single target, per-division lists have
   * one per division that owns ids in this page.
   */
  path: string[];
  /** Ids to fetch from that collection, in any order. */
  ids: string[];
}

/**
 * Builds the Firestore queries that materialize a page of Elastic ids.
 *
 * Never add `orderBy` or range constraints here: Firestore rejects them next to
 * `documentId() in`, and ordering is Elastic's job anyway — the caller re-imposes the
 * Elastic id order after the fetch (see `reconcileListPage`). `extraConstraints` is for
 * equality filters the collection needs regardless of the ids (e.g. a document `type`).
 */
export const buildDocIdQueries = (
  targets: DocIdQueryTarget[],
  extraConstraints: QueryConstraint[] = [],
): Query[] =>
  targets.flatMap(({ path, ids }) => {
    if (!path.length || !ids.length) return [];

    const [root, ...rest] = path;
    const collectionRef = collection(db, root, ...rest);

    return chunkIds(ids, FIRESTORE_IN_LIMIT).map(idChunk =>
      query(collectionRef, where(documentId(), 'in', idChunk), ...extraConstraints),
    );
  });

/**
 * Stable identity for a set of targets. Query objects are rebuilt on every render, so
 * executors key their effects on this string instead of on the query array.
 */
export const getDocIdQueriesKey = (targets: DocIdQueryTarget[]): string =>
  targets
    .filter(({ ids }) => ids.length)
    .map(({ path, ids }) => `${path.join('/')}:${ids.join(',')}`)
    .join('|');

export default buildDocIdQueries;
